import {
  createContext,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { StatsigClient } from '@statsig/js-client';
import { ENV } from 'constants/Global.constants';

const STATSIG_CLIENT_KEY = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY ?? '';
const IS_STATSIG_ENABLED = ENV === 'development' && !!STATSIG_CLIENT_KEY; // Only dev for POC

// Maps NEXT_PUBLIC_POLYAI_ENVIRONMENT values to Statsig-recognized tier names
const STATSIG_TIER_MAP: Record<string, string> = {
  development: 'development',
  staging: 'staging',
  production: 'production',
};
const STATSIG_TIER = STATSIG_TIER_MAP[ENV];

/**
 * Provides the Statsig client instance to the component tree,
 * null when Statsig is not active.
 */
export const StatsigClientContext = createContext<StatsigClient | null>(null);

export const StatsigProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth0();
  const [statsigClient, setStatsigClient] = useState<StatsigClient | null>(
    null,
  );
  const clientRef = useRef<StatsigClient | null>(null);

  useEffect(() => {
    if (!IS_STATSIG_ENABLED || !user?.email) return;

    // Shutdown previous client when user identity changes
    if (clientRef.current) {
      clientRef.current.shutdown();
      clientRef.current = null;
      setStatsigClient(null);
    }

    const client = new StatsigClient(
      STATSIG_CLIENT_KEY,
      {
        userID: user.email,
        email: user.email,
        custom: {
          name: user.name ?? '',
        },
      },
      {
        environment: { tier: STATSIG_TIER },
        disableStorage: true,
      },
    );
    clientRef.current = client;

    client
      .initializeAsync()
      .then(() => {
        // Only set state if this client is still the current one
        if (clientRef.current === client) {
          setStatsigClient(client);
        }
      })
      .catch(err => {
        console.warn(
          'Statsig initialization failed, falling back to server flags',
          err,
        );
      });

    return () => {
      client.shutdown();
      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
  }, [user?.email, user?.name]);

  return (
    <StatsigClientContext.Provider value={statsigClient}>
      {children}
    </StatsigClientContext.Provider>
  );
};
